/**
 * SAP OData client for the Purchase Dashboard.
 *
 * Requests go to same-origin /sap/... paths; src/setupProxy.js forwards them to
 * the gateway with Basic auth attached. See that file for the CORS/TLS reasons
 * the browser cannot reach SAP directly.
 */

const BASE = '/sap/opu/odata/sap';

export const SERVICES = {
  poHeader: `${BASE}/ZPO_DET_SRV/ZIS_PO_DETSet`,
  poItem: `${BASE}/ZIS_PO_ITEM_SRV/ZIS_PO_ITEMSet`,
  poSchedule: `${BASE}/ZPO_SCHEDULE_SRV/ZIS_PO_SCHEDULESet`,
  prItem: `${BASE}/API_PURCHASEREQ_PROCESS_SRV/A_PurchaseRequisitionItem`,
  grHeader: `${BASE}/API_MATERIAL_DOCUMENT_SRV/A_MaterialDocumentHeader`,
  grItem: `${BASE}/API_MATERIAL_DOCUMENT_SRV/A_MaterialDocumentItem`,
  invoice: `${BASE}/API_SUPPLIERINVOICE_PROCESS_SRV/A_SupplierInvoice`,
  vendorOpen: `${BASE}/ZVENDOR_OPEN_SRV/ZET_BSEGSet`,
  vendorMaster: `${BASE}/ZVENDOR_MASTER_SRV/ZVENDOR_MASTERSet`,
};

/**
 * SAP OData V2 serialises dates as /Date(1795478400000)/ (epoch ms, sometimes
 * with a trailing timezone offset). Returns a Date, or null when absent.
 */
export const parseSapDate = (value) => {
  if (!value) return null;
  const match = /\/Date\((-?\d+)/.exec(value);
  if (!match) {
    const direct = new Date(value);
    return isNaN(direct.getTime()) ? null : direct;
  }
  return new Date(Number(match[1]));
};

/** ISO yyyy-mm-dd, or null. Convenient for grouping and axis labels. */
export const toISODate = (value) => {
  const d = value instanceof Date ? value : parseSapDate(value);
  return d && !isNaN(d.getTime()) ? d.toISOString().slice(0, 10) : null;
};

/** SAP decimals arrive as strings ("1000.000"). Coerce, defaulting to 0. */
export const num = (value) => {
  const n = typeof value === 'number' ? value : parseFloat(value);
  return isNaN(n) ? 0 : n;
};

/**
 * Document item numbers are zero-padded inconsistently across services: the PO
 * item service returns "00010" while the material document service returns
 * "10" for the same line. Every cross-service join must go through this, or it
 * silently matches nothing.
 */
export const itemKey = (documentNo, itemNo) =>
  `${String(documentNo || '').trim()}|${parseInt(itemNo, 10)}`;

/** Strip SAP's leading zeros from master-data ids ("0000100005" -> "100005"). */
export const unpadId = (value) => String(value || '').replace(/^0+/, '');

const buildUrl = (endpoint, params = {}) => {
  const qs = new URLSearchParams({ $format: 'json', ...params });
  return `${endpoint}?${qs.toString()}`;
};

/**
 * Fetch one entity set and return its rows. Handles both the V2 envelope
 * ({ d: { results: [] } } / { d: [] }) and the V4 shape ({ value: [] }).
 */
export const fetchEntitySet = async (endpoint, params = {}) => {
  const res = await fetch(buildUrl(endpoint, params), {
    headers: { Accept: 'application/json' },
  });

  if (!res.ok) {
    throw new Error(`${res.status} ${res.statusText} — ${endpoint}`);
  }

  const contentType = res.headers.get('content-type') || '';
  if (!contentType.includes('json')) {
    // Almost always the dev proxy being absent, so SAP's HTML login page comes
    // back instead. Say so plainly rather than dying inside JSON.parse.
    throw new Error(
      `Expected JSON from ${endpoint} but received "${contentType}". ` +
        `Is the dev server running with src/setupProxy.js in place?`
    );
  }

  const json = await res.json();
  if (json?.error) {
    throw new Error(json.error.message?.value || json.error);
  }
  const payload = json?.d ?? json?.value ?? json;
  return Array.isArray(payload) ? payload : payload?.results ?? [];
};

/**
 * Load every entity set the dashboard needs, in parallel.
 *
 * A single failing service must not blank the whole dashboard, so failures are
 * captured per source. Returns { data: { key: rows }, errors: { key: message } }.
 */
export const fetchPurchaseData = async () => {
  const entries = Object.entries(SERVICES);
  const settled = await Promise.all(
    entries.map(async ([key, endpoint]) => {
      try {
        return [key, await fetchEntitySet(endpoint, { $top: '5000' }), null];
      } catch (err) {
        return [key, [], err.message];
      }
    })
  );

  const data = {};
  const errors = {};
  settled.forEach(([key, rows, error]) => {
    data[key] = rows;
    if (error) errors[key] = error;
  });

  return { data, errors };
};
